import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { generatePath, useNavigate, useParams } from "react-router";
import {
  TemplateDecisionCards,
  TemplateModalState
} from "../../../../../components/BuildingBlocks/TemplateDecisionCards/TemplateDecisionCards";
import { TABS as ENERGY_DATA_TABS } from "../../../../../components/EnergyData/EnergyDataView/EnergyDataView";
import { LoadOrError } from "../../../../../components/LoadOrError/LoadOrError";
import { useShouldShowStaffView } from "../../../../../components/StaffViewToggle/useShouldShowStaffView";
import { useToast } from "../../../../../mantine/notifications/useToast";
import { ROUTES } from "../../../../../routes";
import { PaymentIntervallAndBillingMethod } from "../../../../../types/api.types";
import { useParagraph6Companies } from "../../../hooks/useParagraph6Companies";
import { useParagraph6Contract } from "../../../hooks/useParagraph6Contract";
import { useParagraph6ContractMutations } from "../../../hooks/useParagraph6ContractMutations";
import type {
  Paragraph6ContractFormFields,
  Paragraph6ContractPayload,
  Paragraph6ContractResponse
} from "../../../Paragraph6.types";
import { Paragraph6ContractForm } from "./Paragraph6ContractForm/Paragraph6ContractForm";
import { Paragraph6ContractTemplateSelect } from "./Paragraph6ContractTemplateSelect";

const FIKTIVE_MENGEN_TOAST_ID = "fiktive-mengen-toast";

interface Paragraph6ContractModalContentProps {
  contractId?: string;
  onClose: (contractId?: string) => void;
  templates?: Array<Paragraph6ContractResponse>;
  modalState: TemplateModalState;
  setModalState: React.Dispatch<React.SetStateAction<TemplateModalState>>;
  selectedTemplate: Partial<Paragraph6ContractPayload> | null;
  setSelectedTemplate: React.Dispatch<
    React.SetStateAction<Partial<Paragraph6ContractPayload> | null>
  >;
  variantId: number;
  editMode: boolean;
  onContractNameChange: (name: string) => void;
}

function Paragraph6ContractModalContent({
  contractId,
  onClose,
  templates,
  modalState,
  setModalState,
  selectedTemplate,
  setSelectedTemplate,
  variantId,
  editMode,
  onContractNameChange
}: Paragraph6ContractModalContentProps) {
  const { showToast } = useToast();
  const { paragraph6Contract, isLoading: paragraph6ContractLoading } =
    useParagraph6Contract(contractId);

  const [responseError, setResponseError] = useState<Error>();
  const { shouldShowStaffView } = useShouldShowStaffView();

  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const energyDataUploadPath = projectId
    ? generatePath(
        `${ROUTES.energyData.entry}/${ENERGY_DATA_TABS.TAB_ENERGY_DATA_UPLOAD}`,
        { projectId }
      )
    : undefined;

  const defaultParagraph6Contract = {
    billing_method_and_period:
      PaymentIntervallAndBillingMethod.annual_based_on_measurements,
    variant: variantId,
    gemeinde_anteile_erzeuger: []
  } as const satisfies Partial<Paragraph6ContractPayload>;

  const { paragraph6ContractCreateMutation, paragraph6ContractEditMutation } =
    useParagraph6ContractMutations(variantId);

  const isMutating =
    paragraph6ContractCreateMutation.isPending ||
    paragraph6ContractEditMutation.isPending;

  const { paragraph6Companies, isLoading: paragraph6CompaniesLoading } =
    useParagraph6Companies(variantId);

  const hasNoTemplates = !templates?.length;

  const onSubmit: SubmitHandler<Paragraph6ContractFormFields> = (data) => {
    const fiktiveStrommengeActive = data.has_fiktive_strommenge;

    if (!data.has_fiktive_strommenge) {
      data.beteiligung_fiktive_strommengen = null;
    }
    delete data.has_fiktive_strommenge;

    if (!data.has_split_beteiligung) {
      data.beteiligung_nicht_erstattungsfaehig = null;
      data.formula_beteiligung_nicht_erstattungsfaehig = null;
      data.formula_beteiligung_nicht_erstattungsfaehig_text = null;
    }
    delete data.has_split_beteiligung;
    if (data.is_template === undefined) {
      data.is_template = false;
    }

    data.community_iban = (data?.community_iban || "").replace(
      /[^a-zA-Z0-9]/g,
      ""
    );

    const mutation = editMode
      ? paragraph6ContractEditMutation
      : paragraph6ContractCreateMutation;

    mutation.mutate(data, {
      onSuccess: (result) => {
        onClose(result?.data?.id);
        if (fiktiveStrommengeActive) {
          showToast({
            type: "warning",
            message: (
              <>
                Sie haben in Ihrem Vertrag angegeben, dass auch fiktive
                Strommengen berücksichtigt werden. Bitte fügen Sie nun die
                betroffenen Erzeuger hinzu und ergänzen Sie anschließend im
                Bereich Energiedaten die fiktiven Mengen für den im Vertrag
                angegebenen Zeitraum. Wenn Sie bereits alle Erzeuger hinzugefügt
                haben, können Sie auch direkt auf den untenstehenden Button
                klicken.
                {energyDataUploadPath && (
                  <Button
                    mt="sm"
                    variant="default"
                    onClick={() => {
                      navigate(energyDataUploadPath);
                      notifications.hide(FIKTIVE_MENGEN_TOAST_ID);
                    }}
                  >
                    Fiktive Strommengen bereitstellen
                  </Button>
                )}
              </>
            ),
            py: "md",
            autoClose: false,
            withCloseButton: true,
            // the theme scrolls the body at 180px and Notifications clips the
            // root at notificationMaxHeight (200px), both hiding the
            // Fiktive Strommengen bereitstellen button
            style: { maxHeight: "none" },
            styles: { body: { maxHeight: "none" } },
            id: FIKTIVE_MENGEN_TOAST_ID
          });
        }
      },
      onError: (error) => {
        setResponseError(error);
      }
    });
  };

  const showContractForm =
    modalState === TemplateModalState.NewContract ||
    editMode ||
    (modalState === TemplateModalState.NewTemplateContract &&
      !!selectedTemplate) ||
    hasNoTemplates;

  const contract =
    paragraph6Contract ||
    (modalState === TemplateModalState.NewTemplateContract &&
      selectedTemplate) ||
    defaultParagraph6Contract;

  if (showContractForm) {
    const isLoading =
      paragraph6CompaniesLoading || (editMode && paragraph6ContractLoading);

    return (
      <LoadOrError
        loading={isLoading}
        loadingMessage="Vertrag wird geladen ..."
      >
        <Paragraph6ContractForm
          isNewContractWithoutReference={
            !editMode &&
            (modalState === TemplateModalState.NewContract || hasNoTemplates)
          }
          isStaff={shouldShowStaffView}
          isSubmitting={isMutating}
          paragraph6Companies={paragraph6Companies ?? []}
          paragraph6Contract={contract}
          submissionError={responseError}
          onCancel={onClose}
          onContractNameChange={onContractNameChange}
          onSubmit={onSubmit}
        />
      </LoadOrError>
    );
  }

  if (modalState === TemplateModalState.None) {
    return <TemplateDecisionCards setModalState={setModalState} />;
  }

  return (
    <Paragraph6ContractTemplateSelect
      templates={templates}
      onChange={setSelectedTemplate}
    />
  );
}

export { Paragraph6ContractModalContent };
